import type {
  SpecialType,
  StoreSpecialReservationModes,
} from '@iheartjane/types';
import capitalize from 'lodash-es/capitalize';
import isNil from 'lodash-es/isNil';
import truncate from 'lodash-es/truncate';

const abbreviateLabel = (label: string, type: string) => {
  const matches = label.match(
    /(?<dollar>\$?)(?<rate>\d+\.?\d*)(?<percent>%?)/i
  );

  if (isNil(matches)) return 'Deal';

  if (matches?.groups?.['dollar']) {
    if (type === 'target_price') {
      return `$${matches?.groups?.['rate']}`;
    } else {
      return `$${matches?.groups?.['rate']} OFF`;
    }
  } else {
    return `${Math.round(parseFloat(matches?.groups?.['rate'] || ''))}% OFF`;
  }
};

export const getDiscountLabel = ({
  discountAmount,
  discountType,
  specialType,
  storeComplianceLanguageSettings,
}: {
  discountAmount?: string | null;
  discountType: string;
  specialType: SpecialType;
  storeComplianceLanguageSettings?: Record<string, string | null> | null;
}) => {
  switch (specialType) {
    case 'bundle':
      return storeComplianceLanguageSettings?.['bundle']
        ? truncate(
            capitalize(storeComplianceLanguageSettings['bundle'] || ''),
            { length: 10 }
          )
        : 'Bundle';
    case 'spending_threshold':
      if (discountType === 'target_price') {
        return 'Deal';
      } else {
        return abbreviateLabel(discountAmount || '', discountType || '');
      }
    default:
      return abbreviateLabel(discountAmount || '', discountType || '');
  }
};

export const getSpecialType = (
  specialType: SpecialType,
  storeComplianceLanguageSettings?: Record<string, string | null> | null
) => {
  switch (specialType) {
    case 'cart_total':
      return 'Cart Total';
    case 'product':
      return 'Product';
    case 'bundle':
      return (
        capitalize(storeComplianceLanguageSettings?.['bundle'] || '') ||
        'Bundle'
      );
    case 'spending_threshold':
      return 'Spend X Get Y';
    case 'bulk_pricing':
      return 'Bulk';
    default:
      return 'Deal';
  }
};

type SpecialReservationMode = 'pickup' | 'delivery';

export const hasSingleReservationMode = (
  reservationModes?: StoreSpecialReservationModes | null
): SpecialReservationMode | false => {
  const availableModes = Object.fromEntries(
    Object.entries(reservationModes || {}).filter(
      ([, isAvailable]) => isAvailable
    )
  );

  // currently we're supporting only pickup and delivery
  const modes = Object.keys(availableModes).filter(
    (mode) => mode !== 'kiosk' && mode !== 'curbside'
  );

  if (modes.length === 0 || modes.length > 1) return false;
  return modes[0] as SpecialReservationMode;
};
